// Admin → Bank Manager — manage user wallets (cash + points) with full
// audit ledger. Designed for the founder to credit creators a bonus,
// fix tipping disputes, or grant points without touching the database.
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  Search, Wallet, Coins, DollarSign, Banknote, Receipt, ArrowUpRight,
  ArrowDownRight, AlertCircle, User as UserIcon, Loader2, History,
} from "lucide-react";

function fmtUSD(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}
function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function BankManagerTab() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // user object
  const [ledger, setLedger] = useState([]);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grant, setGrant] = useState({ amount: "", kind: "cash", reason: "" });
  const [busy, setBusy] = useState(false);

  const search = async (term = q) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/bank/users", { params: { q: term, limit: 25 } });
      setUsers(r.data?.users || []);
      setTotals(r.data?.totals || {});
    } catch (e) {
      toast.error("Couldn't load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { search(""); }, []); // initial load

  const onSelect = async (u) => {
    setSelected(u);
    setLedger([]);
    try {
      const r = await api.get("/admin/bank/ledger", { params: { user_id: u.user_id, limit: 30 } });
      setLedger(r.data?.entries || []);
    } catch {/* ignore */}
  };

  const submitGrant = async (signMultiplier) => {
    const amt = parseFloat(grant.amount);
    if (Number.isNaN(amt) || amt <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    if (!grant.reason.trim()) {
      toast.error("Reason is required (e.g. 'Bonus for hitting 10k followers')");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/bank/credit", {
        user_id: selected.user_id,
        amount: amt * signMultiplier,
        kind: grant.kind,
        reason: grant.reason.trim(),
      });
      const verb = signMultiplier > 0 ? "Credited" : "Debited";
      const unit = grant.kind === "cash" ? fmtUSD(amt) : `${amt.toFixed(0)} pts`;
      toast.success(`${verb} ${unit} → ${selected.name || selected.email}. New balance: ${grant.kind === "cash" ? fmtUSD(r.data.balance_after) : `${r.data.balance_after} pts`}`);
      setGrantOpen(false);
      setGrant({ amount: "", kind: "cash", reason: "" });
      // Refresh just the selected user + ledger
      const u = await api.get(`/admin/bank/user/${selected.user_id}`);
      setSelected(u.data);
      const led = await api.get("/admin/bank/ledger", { params: { user_id: selected.user_id, limit: 30 } });
      setLedger(led.data?.entries || []);
      // Also refresh the list so totals reflect
      search(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bank action failed");
    } finally {
      setBusy(false);
    }
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    search(q);
  };

  return (
    <div data-testid="bank-manager-tab" className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-display font-black tracking-tight flex items-center gap-2">
            <Banknote className="w-6 h-6 text-tv-accent" /> Bank Manager
          </h2>
          <p className="text-white/60 text-sm">Credit / debit user wallets &amp; points. Every action is logged.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-white/5 ring-1 ring-white/10 px-3 py-2">
            <div className="text-[9px] uppercase tracking-[0.2em] text-white/50 font-bold">Total Owed</div>
            <div className="text-xl font-display font-black text-emerald-300" data-testid="bank-total-owed">{fmtUSD(totals.total_owed_usd)}</div>
          </div>
          <div className="rounded-lg bg-white/5 ring-1 ring-white/10 px-3 py-2">
            <div className="text-[9px] uppercase tracking-[0.2em] text-white/50 font-bold">Creators w/ Balance</div>
            <div className="text-xl font-display font-black text-tv-accent" data-testid="bank-creator-count">{totals.creators_with_balance ?? 0}</div>
          </div>
        </div>
      </div>

      <form onSubmit={onSearchSubmit} className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <input
          data-testid="bank-search-input"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email, name, or user_id…"
          className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-black/40 ring-1 ring-white/15 text-sm text-white placeholder:text-white/40 focus:ring-tv-accent outline-none"
        />
      </form>

      <div className="grid lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-5">
        {/* User list */}
        <div className="rounded-xl bg-white/5 ring-1 ring-white/10 overflow-hidden" data-testid="bank-user-list">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-white/55 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-right px-3 py-2">Cash</th>
                <th className="text-right px-3 py-2">Points</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="text-center py-8 text-white/40"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={4} className="text-center py-10 text-white/40">No users match.</td></tr>
              )}
              {users.map((u) => {
                const active = selected?.user_id === u.user_id;
                return (
                  <tr
                    key={u.user_id}
                    onClick={() => onSelect(u)}
                    data-testid={`bank-user-row-${u.user_id}`}
                    className={`border-t border-white/5 cursor-pointer ${active ? "bg-tv-accent/10" : "hover:bg-white/5"}`}
                  >
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" /> : <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center"><UserIcon className="w-3.5 h-3.5 text-white/50" /></div>}
                        <div className="min-w-0">
                          <div className="font-bold text-xs truncate">{u.name || u.email}</div>
                          <div className="text-[10px] text-white/45 truncate">{u.email} · {u.role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold text-emerald-300">{fmtUSD(u.pending_earnings)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-tv-accent">{(u.points_balance || 0).toLocaleString()}</td>
                    <td className="px-2 py-2.5 text-right text-white/30">›</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Detail / actions panel */}
        <div className="space-y-4">
          {!selected ? (
            <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-8 text-center text-white/40 text-sm" data-testid="bank-no-selection">
              <Wallet className="w-12 h-12 mx-auto mb-3 opacity-30" />
              Pick a user from the list to manage their wallet.
            </div>
          ) : (
            <>
              <div className="rounded-xl bg-gradient-to-br from-tv-accent/10 via-black to-black ring-1 ring-tv-accent/30 p-4" data-testid="bank-detail-panel">
                <div className="flex items-start gap-3">
                  {selected.avatar_url ? <img src={selected.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center"><UserIcon className="w-5 h-5 text-white/50" /></div>}
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-black text-lg truncate">{selected.name || selected.email}</div>
                    <div className="text-[11px] text-white/55 truncate">{selected.email} · {selected.user_id}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-tv-accent font-bold mt-0.5">{selected.role}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="rounded-lg bg-black/40 ring-1 ring-emerald-500/20 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold flex items-center gap-1"><DollarSign className="w-3 h-3" /> Cash balance</div>
                    <div className="text-2xl font-display font-black text-emerald-300 mt-0.5" data-testid="bank-cash-balance">{fmtUSD(selected.pending_earnings)}</div>
                  </div>
                  <div className="rounded-lg bg-black/40 ring-1 ring-tv-accent/20 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-tv-accent font-bold flex items-center gap-1"><Coins className="w-3 h-3" /> Points</div>
                    <div className="text-2xl font-display font-black text-tv-accent mt-0.5" data-testid="bank-points-balance">{(selected.points_balance || 0).toLocaleString()}</div>
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => { setGrant({ amount: "", kind: "cash", reason: "" }); setGrantOpen(true); }}
                    data-testid="bank-open-grant"
                    className="flex-1 bg-tv-accent hover:bg-yellow-300 text-black font-bold text-xs py-2 rounded-lg uppercase tracking-wider flex items-center justify-center gap-1.5"
                  >
                    <ArrowUpRight className="w-3.5 h-3.5" /> Add funds / Points
                  </button>
                  <button
                    type="button"
                    onClick={() => { setGrant({ amount: "", kind: "cash", reason: "" }); setGrantOpen(true); }}
                    data-testid="bank-open-debit"
                    className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-300 ring-1 ring-red-500/30 font-bold text-xs py-2 rounded-lg uppercase tracking-wider flex items-center justify-center gap-1.5"
                  >
                    <ArrowDownRight className="w-3.5 h-3.5" /> Debit
                  </button>
                </div>
              </div>

              {/* Grant form modal */}
              {grantOpen && (
                <div className="rounded-xl bg-black/60 ring-1 ring-white/15 p-4 space-y-3" data-testid="bank-grant-form">
                  <div className="text-sm font-bold flex items-center gap-2"><Receipt className="w-4 h-4 text-tv-accent" /> Adjust wallet for <span className="text-tv-accent">{selected.name || selected.email}</span></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold block mb-1">Kind</label>
                      <select
                        data-testid="bank-grant-kind"
                        value={grant.kind}
                        onChange={(e) => setGrant((p) => ({ ...p, kind: e.target.value }))}
                        className="w-full px-3 py-2 bg-black/40 ring-1 ring-white/15 rounded-lg text-sm focus:ring-tv-accent outline-none"
                      >
                        <option value="cash">Cash (USD)</option>
                        <option value="points">Points</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold block mb-1">Amount {grant.kind === "cash" ? "(USD)" : "(points)"}</label>
                      <input
                        data-testid="bank-grant-amount"
                        type="number"
                        step={grant.kind === "cash" ? "0.01" : "1"}
                        min="0"
                        value={grant.amount}
                        onChange={(e) => setGrant((p) => ({ ...p, amount: e.target.value }))}
                        placeholder={grant.kind === "cash" ? "10.00" : "100"}
                        className="w-full px-3 py-2 bg-black/40 ring-1 ring-white/15 rounded-lg text-sm focus:ring-tv-accent outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold block mb-1">Reason (audit-logged)</label>
                    <input
                      data-testid="bank-grant-reason"
                      type="text"
                      value={grant.reason}
                      onChange={(e) => setGrant((p) => ({ ...p, reason: e.target.value }))}
                      placeholder="e.g. Bonus for hitting 10k followers · Tip dispute reversal"
                      className="w-full px-3 py-2 bg-black/40 ring-1 ring-white/15 rounded-lg text-sm focus:ring-tv-accent outline-none"
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => submitGrant(1)}
                      data-testid="bank-submit-credit"
                      className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-black uppercase tracking-wider text-xs py-2.5 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50"
                    >
                      <ArrowUpRight className="w-3.5 h-3.5" /> Credit
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => submitGrant(-1)}
                      data-testid="bank-submit-debit"
                      className="flex-1 bg-red-500 hover:bg-red-400 text-black font-black uppercase tracking-wider text-xs py-2.5 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50"
                    >
                      <ArrowDownRight className="w-3.5 h-3.5" /> Debit
                    </button>
                    <button
                      type="button"
                      onClick={() => setGrantOpen(false)}
                      data-testid="bank-cancel-grant"
                      className="px-3 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-bold text-white/60"
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="text-[10px] text-yellow-300 flex items-start gap-1.5">
                    <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                    Every credit / debit is recorded in the bank ledger with your admin id, IP, and timestamp.
                  </div>
                </div>
              )}

              {/* Ledger */}
              <div className="rounded-xl bg-white/5 ring-1 ring-white/10 overflow-hidden" data-testid="bank-ledger">
                <div className="px-3 py-2 bg-black/40 text-[10px] uppercase tracking-[0.2em] text-white/55 font-bold flex items-center gap-1.5 border-b border-white/10">
                  <History className="w-3 h-3" /> Recent transactions ({ledger.length})
                </div>
                {ledger.length === 0 ? (
                  <div className="text-center py-6 text-white/30 text-xs">No transactions yet.</div>
                ) : (
                  <ul className="divide-y divide-white/5">
                    {ledger.map((e) => {
                      const credit = (e.amount || 0) > 0;
                      return (
                        <li key={e.id} className="px-3 py-2 flex items-center gap-3 text-xs">
                          {credit ? <ArrowUpRight className="w-4 h-4 text-emerald-400 shrink-0" /> : <ArrowDownRight className="w-4 h-4 text-red-400 shrink-0" />}
                          <div className="min-w-0 flex-1">
                            <div className="font-bold truncate">{e.reason}</div>
                            <div className="text-[10px] text-white/45">by {e.admin_name || "admin"} · {fmtTime(e.created_at)}</div>
                          </div>
                          <div className={`font-mono font-bold shrink-0 ${credit ? "text-emerald-300" : "text-red-300"}`}>
                            {credit ? "+" : ""}{e.kind === "cash" ? fmtUSD(e.amount) : `${e.amount} pts`}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
